import { CategoryAxis, ColumnSeries, ValueAxis, XYChart } from "@amcharts/amcharts4/charts";
import { color, create, Tooltip } from "@amcharts/amcharts4/core";
import _ from "lodash";
import React, { Component } from "react";
import WebFont from "webfontloader";
import LoadingEllipsis from "../../Loading/LoadingEllipsis";
import { disposeChart } from "../helpers/chartHelpers";
import { configureCategoryAxis, configureValueAxis } from "../helpers/configurations";
import { oceChartConfiguration } from "../helpers/oceConfigurations";
import { ThemeInt } from "../helpers/types";

interface Props {
  data: Array<Record<string, number | any>>;
  theme: ThemeInt;
  chartId: string;
  colorMapping: Record<string, string>;
  isLoading: boolean;
}

class VocSalesTopicChart extends Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  chart!: XYChart;
  chartId = this.props.chartId;
  appliedWidthPx?: number;

  componentDidMount() {
    WebFont.load({
      custom: {
        families: ["nissan"],
        urls: ["../../../assets/fonts/fonts.css"],
      },
      // @ts-ignore
      active: this.initChart(),
      timeout: 8000,
    });
  }

  componentDidUpdate(prevProps: any) {
    //Handle refreshing the chart when the dataset changes
    if (!_.isEqual(prevProps, this.props)) {
      disposeChart(this.chartId).then((res) => {
        this.initChart();
      });
    }
  }

  componentWillUnmount() {
    disposeChart(this.chartId);
  }

  initChart() {
    const { data, theme, colorMapping } = this.props;
    this.chart = create(this.chartId, XYChart);
    oceChartConfiguration(this.chart);
    this.chart.data = data;
    this.chart.numberFormatter.numberFormat = "#a";
    


    // Create Axes
    const categoryAxis = this.chart.xAxes.push(new CategoryAxis());
    categoryAxis.dataFields.category = "topic";
    configureCategoryAxis(categoryAxis, theme);
    categoryAxis.renderer.minGridDistance = 40;
    categoryAxis.renderer.minHeight = 80;         // or 90/110 depending on font
    categoryAxis.renderer.labels.template.verticalCenter = "top";
    categoryAxis.renderer.labels.template.dy = 10;

    const label = categoryAxis.renderer.labels.template;

    label.wrap = true;
    label.truncate = false;

    (label as any).oversizedBehavior = "wrap";
    (label as any).breakWords = false;

    // categoryAxis.events.on("sizechanged", function (ev) {
    //   const axis = ev.target;
    //   const visible = Math.max(1, axis.endIndex - axis.startIndex);

    //   const cellWidth = axis.pixelWidth / visible;
    //   // axis.renderer.labels.template.maxWidth = Math.max(60, cellWidth); // 👈 clamp
    // });


    const valueAxis = this.chart.yAxes.push(new ValueAxis());
    configureValueAxis(valueAxis, theme);
    

    this.chart.data.sort(function (a, b) {
      //@ts-ignore
      return b.topic_score - a.topic_score;
    });

    this.chart.events.on("datavalidated", () => {
      const data = this.chart.data as any[];

      // 1) longest single word
      let longestWord = "";
      for (const d of data) {
        for (const w of String(d.topic ?? "").split(/\s+/)) {
          if (w.length > longestWord.length) longestWord = w;
        }
      }

      // 2) measure it
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.font = "14px nissan";
      const longestWordPx = ctx.measureText(longestWord).width;

      const minCellWidth = Math.ceil(longestWordPx + 24); // padding
      categoryAxis.renderer.labels.template.maxWidth = minCellWidth;

      // 3) compute required vs available width (scoped!)
      const el = document.getElementById(this.chartId);
      if (!el) return;

      const scroller = el.parentElement as HTMLElement | null; //  the chartScroller wrapping THIS chart
      const availableWidth = scroller?.clientWidth ?? 0;
      if (!availableWidth) return;

      const requiredWidth = data.length * minCellWidth;

      if (requiredWidth <= availableWidth) {
        //  few categories → fill container
        el.style.width = "100%";
        if (this.chart.svgContainer) this.chart.svgContainer.htmlElement.style.width = "100%";
      } else {
        //  many categories → fixed width + scroll
        const totalWidth = Math.ceil(requiredWidth);
        el.style.width = `${totalWidth}px`;
        if (this.chart.svgContainer) this.chart.svgContainer.htmlElement.style.width = `${totalWidth}px`;
      }

      // 4) re-layout (amCharts 4)
      (this.chart as any).invalidateLayout?.();
      this.chart.invalidate();
    });


    const series = this.chart.series.push(new ColumnSeries());
    series.columns.template.adapter.add("fill", (fill: any, target: any) => {
      if (target.dataItem.dataContext?.topic_category) {
        const category = target.dataItem.dataContext.topic_category;
        return color(colorMapping[category]);
      } else {
        return fill;
      }
    });
    series.columns.template.adapter.add("stroke", (fill: any, target: any) => {
      if (target.dataItem.dataContext?.topic_category) {
        const category = target.dataItem.dataContext.topic_category;
        return color(colorMapping[category]);
      } else {
        return fill;
      }
    });
    series.dataFields.categoryX = "topic";
    series.dataFields.valueY = "topic_score";
    series.columns.template.tooltip = new Tooltip();
    series.columns.template.tooltipHTML = ``;
    series.columns.template.adapter.add("tooltipHTML", (value: any, target: any) => {
      const comments = target?.tooltipDataItem?.dataContext?.comments;

      if (comments) {
        let commentsHTML = ``;
        comments.forEach((comment: any) => {
          commentsHTML += `<li key=${comment}>● ${comment}</li>`;
        });
        return `
          <ol style="font-size: var(--regular_font_size);">
          ${commentsHTML}            
          </ol>
        `;
      }
    });
    if (series.columns.template.tooltip) {
      series.columns.template.tooltip.label.maxWidth = 200;
      series.columns.template.tooltip.label.wrap = true;
    }
  }

  render() {
    return (
      <div className="chartWrapper">
        <div className="chartScroller">
          <div id={this.props.chartId} data-test-id={this.props.chartId} className="graph" />
        </div>
        <LoadingEllipsis isLoading={this.props.isLoading} />
      </div>
    );
  }
}

export default VocSalesTopicChart;
